import React, { useEffect, useState } from 'react';
import {
	Alert,
	Box,
	Button,
	Card,
	CardContent,
	LinearProgress,
	Stack,
	TextField,
	Typography,
} from '@mui/material';
import { useRecordContext } from 'react-admin';
import {
	AgentBudget,
	getProjectBudget,
	setProjectBudget,
} from '../../Services/agents';

// #region ProjectAgentsBudget
// Plafond mensuel de dépense LLM des agents IA du projet. Au plafond, les agents
// surveillent toujours mais n'appellent plus le LLM jusqu'au 1er (alerte Slack à 80 % et
// 100 %). Estimation aux tarifs configurés sur api-agents, pas la facture Anthropic.
export const ProjectAgentsBudget: React.FC = () => {
	const record = useRecordContext();
	const folderId: string | undefined = record?.folderId;
	const [budget, setBudget] = useState<AgentBudget | null>(null);
	const [value, setValue] = useState('');
	const [error, setError] = useState<string | null>(null);
	const [saving, setSaving] = useState(false);

	useEffect(() => {
		if (!folderId) return;
		getProjectBudget(folderId)
			.then((b) => {
				setBudget(b);
				setValue(b.source === 'project' ? String(b.limit_usd) : '');
			})
			.catch(() => setBudget(null));
	}, [folderId]);

	const save = (usd: number | null) => {
		if (!folderId) return;
		setSaving(true);
		setError(null);
		setProjectBudget(folderId, usd)
			.then((b) => {
				setBudget(b);
				setValue(b.source === 'project' ? String(b.limit_usd) : '');
			})
			.catch((e: any) => setError(e?.message || 'Enregistrement impossible'))
			.finally(() => setSaving(false));
	};

	if (!folderId || !budget) return null;
	return (
		<Card sx={{ mt: 2 }}>
			<CardContent>
				<Typography variant="subtitle1">Agents IA — plafond mensuel</Typography>
				<Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
					{budget.spent_usd.toFixed(2)} $ dépensés sur{' '}
					{budget.limit_usd.toFixed(2)} $ en {budget.month} (
					{budget.source === 'project'
						? 'plafond du projet'
						: `plafond par défaut, ${budget.default_usd} $`}
					).
				</Typography>
				<LinearProgress
					variant="determinate"
					value={Math.min(100, budget.percent)}
					color={
						budget.exceeded
							? 'error'
							: budget.percent >= 80
								? 'warning'
								: 'primary'
					}
				/>
				{budget.exceeded && (
					<Alert severity="error" sx={{ mt: 1 }}>
						Plafond atteint : les agents surveillent toujours mais n'écrivent
						plus rien jusqu'au {budget.resets_at}.
					</Alert>
				)}
				<Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 2 }}>
					<TextField
						size="small"
						type="number"
						label="Plafond du projet ($)"
						placeholder={String(budget.default_usd)}
						value={value}
						onChange={(e) => setValue(e.target.value)}
						sx={{ width: 170 }}
					/>
					<Button
						variant="contained"
						size="small"
						disabled={saving || value === ''}
						onClick={() => save(Number(value))}
					>
						Enregistrer
					</Button>
					<Button
						size="small"
						disabled={saving || budget.source !== 'project'}
						onClick={() => save(null)}
					>
						Revenir au défaut
					</Button>
				</Stack>
				{error && (
					<Box sx={{ mt: 1 }}>
						<Alert severity="error">{error}</Alert>
					</Box>
				)}
				<Typography
					variant="caption"
					color="text.secondary"
					display="block"
					sx={{ mt: 1 }}
				>
					Estimation aux tarifs configurés (LLM seulement ; BigQuery est payé
					par le projet GCP du client).
				</Typography>
			</CardContent>
		</Card>
	);
};
// #endregion
